import Close from "@mui/icons-material/Close";
import WarningAmber from "@mui/icons-material/WarningAmber";
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { addTableToApp } from "@/features/app-studio/api/appTablesApi";
import { useAddToAppTarget } from "@/features/app-studio/hooks/useAddToAppTarget";
import { AddToAppTargetSelect } from "@/features/app-studio/ui/AddToAppTargetSelect";
import { AppChartColumnSpanSelect } from "@/features/app-studio/ui/AppChartColumnSpanSelect";
import { APP_CHART_GRID_COLUMNS } from "@/features/app-studio/lib/appChartLayout";

type AddTableToAppDialogProps = {
  open: boolean;
  onClose: () => void;
  sql: string | null;
};

export function AddTableToAppDialog({ open, onClose, sql }: AddTableToAppDialogProps) {
  const { t } = useTranslation("appStudio");
  const navigate = useNavigate();
  const target = useAddToAppTarget(open);
  const { reset: resetTarget, selectedAppId, writeAppId, tabIdForPayload, isTargetComplete } =
    target;

  const [description, setDescription] = useState("");
  const [columnSpan, setColumnSpan] = useState<number>(APP_CHART_GRID_COLUMNS);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const resetState = useCallback(() => {
    resetTarget();
    setDescription("");
    setColumnSpan(APP_CHART_GRID_COLUMNS);
    setIsSaving(false);
    setSaveError(null);
  }, [resetTarget]);

  useEffect(() => {
    if (open) {
      resetState();
    }
  }, [open, resetState]);

  const handleClose = () => {
    if (isSaving) {
      return;
    }
    onClose();
  };

  const handleConfirm = async () => {
    if (!sql || selectedAppId === "" || writeAppId === null || !isTargetComplete) {
      return;
    }
    setIsSaving(true);
    setSaveError(null);
    try {
      await addTableToApp(writeAppId, {
        sql,
        description: description.trim() || null,
        column_span: columnSpan,
        tab_id: tabIdForPayload,
      });
      onClose();
      navigate(`/app-studio/${selectedAppId}`, { state: { activeTabId: tabIdForPayload } });
    } catch (error) {
      const message = error instanceof Error ? error.message : t("addTable.errors.saveFailed");
      setSaveError(message);
      setIsSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={(_event, reason) => {
        if (reason === "backdropClick" || isSaving) {
          return;
        }
        handleClose();
      }}
      fullWidth
      maxWidth="sm"
      slotProps={{
        paper: {
          sx: {
            bgcolor: "var(--theme-surface)",
            backgroundImage: "none",
            border: "1px solid var(--theme-border)",
            borderRadius: 2,
          },
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography variant="h6" component="span" sx={{ fontWeight: 700 }}>
          {t("addTable.title")}
        </Typography>
        <IconButton
          aria-label={t("addTable.closeAria")}
          onClick={handleClose}
          disabled={isSaving}
          size="small"
          edge="end"
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        <Stack spacing={2}>
          <Typography variant="body2" sx={{ color: "var(--theme-text-secondary)" }}>
            {t("addTable.description")}
          </Typography>

          <TextField
            label={t("addTable.fields.description")}
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            disabled={isSaving}
            fullWidth
            multiline
            minRows={2}
            placeholder={t("addTable.fields.descriptionPlaceholder")}
          />

          {!sql ? (
            <Alert severity="warning">{t("addTable.noSql")}</Alert>
          ) : null}

          <AddToAppTargetSelect
            idPrefix="add-table"
            target={target}
            disabled={isSaving || !sql}
            onTargetChange={() => setSaveError(null)}
          />

          <AppChartColumnSpanSelect
            value={columnSpan}
            disabled={isSaving || !sql}
            onChange={setColumnSpan}
          />

          {saveError ? (
            <Alert severity="error" icon={<WarningAmber fontSize="inherit" />}>
              {saveError}
            </Alert>
          ) : null}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid var(--theme-border)" }}>
        <Button onClick={handleClose} disabled={isSaving} sx={{ textTransform: "none" }}>
          {t("addTable.cancel")}
        </Button>
        <Button
          variant="contained"
          onClick={() => void handleConfirm()}
          disabled={isSaving || !sql || !isTargetComplete}
          sx={{ textTransform: "none" }}
        >
          {isSaving ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={18} color="inherit" />
              <span>{t("addTable.adding")}</span>
            </Stack>
          ) : (
            t("addTable.confirm")
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
